/**
 * TanStack Query hooks for MATCH decisions — the agreed set.
 *
 * Separate from `useSurfacerDecisions`, which covers findings the report did NOT
 * mention (a 6-way clinical verdict). This covers findings it DID mention, where
 * the only question is whether the match itself is right.
 *
 * MATCH IS IMPLICIT. A correct match is the absence of a row; reviewers only
 * click to disagree, which stores `wrong`. Clicking again stores `pending`, an
 * undo. There is no "matched" value to send.
 *
 * The per-study `opened` flag closes a gap the flags alone can't: without it, a
 * study with zero `wrong` rows is indistinguishable from one nobody looked at.
 * It records that the section was EXPANDED — not that any match was examined.
 * See the backend `put_matches_opened` docstring before using it in any rate.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export type MatchDecision = "wrong" | "pending";

export type MatchesMap = Record<string, MatchDecision>;

interface MatchesResponse {
  matches: MatchesMap;
  opened: boolean;
}

const SURFACER_MATCHES_ROOT = ["surfacer-matches"] as const;

export const surfacerMatchKeys = {
  all: SURFACER_MATCHES_ROOT,
  study: (studyUid: string, cohortId: string) =>
    [...SURFACER_MATCHES_ROOT, "study", cohortId, studyUid] as const,
};

async function fetchMatches(cohortId: string, studyUid: string): Promise<MatchesResponse> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  const response = await axios.get<MatchesResponse>(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer/matches`,
    { withCredentials: true },
  );
  return response.data;
}

async function postMatch(
  cohortId: string,
  studyUid: string,
  finding: string,
  decision: MatchDecision,
): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  await axios.post(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer/match`,
    { finding, decision },
    { withCredentials: true },
  );
}

async function postOpened(cohortId: string, studyUid: string): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  await axios.post(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer/matches/opened`,
    {},
    { withCredentials: true },
  );
}

export function useSurfacerMatches(cohortId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const queryKey = surfacerMatchKeys.study(studyUid, cohortId);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchMatches(cohortId, studyUid),
    enabled: !!studyUid && !!cohortId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const flagMutation = useMutation({
    mutationFn: ({ finding, decision }: { finding: string; decision: MatchDecision }) =>
      postMatch(cohortId, studyUid, finding, decision),
    onMutate: async ({ finding, decision }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MatchesResponse>(queryKey);
      queryClient.setQueryData<MatchesResponse>(queryKey, {
        matches: { ...previous?.matches, [finding]: decision },
        opened: previous?.opened ?? false,
      });
      return { previous };
    },
    onError: (err, vars, context) => {
      console.error(
        `surfacer match write failed for ${vars.finding} (${vars.decision}); rolling back`,
        err,
      );
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const openedMutation = useMutation({
    mutationFn: () => postOpened(cohortId, studyUid),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MatchesResponse>(queryKey);
      queryClient.setQueryData<MatchesResponse>(queryKey, {
        matches: previous?.matches ?? {},
        opened: true,
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      // Rolling back matters: leaving `opened` optimistically true would claim
      // the section was reviewed when the write never landed.
      console.error("surfacer matches-opened write failed; rolling back", err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const opened = query.data?.opened ?? false;

  return {
    matches: query.data?.matches ?? {},
    opened,
    isLoading: query.isLoading,
    isError: flagMutation.isError,
    /** Flag a match wrong, or pass `pending` to clear the flag. */
    setMatchDecision: (finding: string, decision: MatchDecision) => {
      flagMutation.mutate({ finding, decision });
    },
    /** Fired on first expand only — the GET tells us whether it's already set. */
    markOpened: () => {
      if (!opened && !openedMutation.isPending) openedMutation.mutate();
    },
  };
}
